import { domain, nonNullable } from '@shared/lib/utils'
import { type Metadata } from 'next'
import { appName } from '@shared/lib/jurisdiction'
import { pathMappings } from '@shared/lib/paths'
import { type DocumentType } from '@shared/lib/norms.types'

const PAGE_POSTFIX = `\u2002|\u2002${appName}`

/**
 * Category and sub-category labels for the OG image's breadcrumb line, derived
 * from the path so every page gets them without touching its own
 * `generateMetadata`. The third segment is skipped: it is the norm itself,
 * which the image already shows as the title.
 */
function getCrumbs(currentPath: string): string[] {
  const [category, subCategory] = currentPath.replace(/^\//, '').split('/')
  const categoryMapping = pathMappings[category]
  if (!categoryMapping) return []
  const subCategoryName = subCategory
    ? categoryMapping.paths?.[subCategory]?.name
    : undefined
  return [categoryMapping.name, subCategoryName].filter(nonNullable)
}

function getOgImageUrl(
  {
    title,
    description,
    ogDescription,
    imageTemplate,
    imageDocumentType,
    imageTitle,
    imageAbbreviation,
    imageCounts,
    imageResults,
  }: MetaProps,
  currentPath: string,
): string {
  const params = new URLSearchParams({
    title: imageTitle ?? title ?? '',
    subTitle: ogDescription ?? description ?? '',
    template: imageTemplate ?? 'generic',
  })
  if (imageDocumentType) params.set('type', imageDocumentType)
  if (imageAbbreviation) params.set('abbr', imageAbbreviation)
  // Every list travels as one param per entry rather than as a joined string:
  // labels like the court name 'LG Ried/Innkreis' carry their own separator,
  // whatever we'd pick.
  for (const count of imageCounts ?? []) params.append('counts', count)
  for (const result of imageResults ?? []) {
    params.append('results', result.label)
    params.append('resultTypes', result.type)
  }
  for (const crumb of getCrumbs(currentPath)) params.append('crumbs', crumb)
  return `/api/og?${params.toString()}`
}

export interface MetaProps {
  /** Title of the page */
  title?: string
  /** Description of the page */
  description?: string
  ogDescription?: string
  canonical?: string
  titlePostfix?: string
  imageTemplate?: 'generic' | 'norm' | 'search' | 'home'
  /** Picks the `norm` template's icon: scales for Judikatur, book otherwise. */
  imageDocumentType?: DocumentType
  /**
   * `title` without its trailing abbreviation. Set together with
   * `imageAbbreviation` so the OG image can put the abbreviation on its own
   * line while the meta tags keep the combined form.
   */
  imageTitle?: string
  imageAbbreviation?: string
  /** Pre-formatted result tallies, e.g. `['87 Entscheidungen', '2 Gesetze']`. */
  imageCounts?: string[]
  /** Top hits listed under the tallies; `type` picks each row's icon. */
  imageResults?: Array<{ type: string; label: string }>
}

export function getMetadata(props: MetaProps, currentPath: string): Metadata {
  const {
    title,
    description,
    ogDescription,
    canonical,
    titlePostfix = PAGE_POSTFIX,
  } = props
  return {
    applicationName: appName,
    metadataBase: new URL(domain),
    title: `${title ?? ''}${titlePostfix}`,
    description,
    alternates: {
      canonical: canonical ?? currentPath,
    },
    openGraph: {
      type: 'website',
      url: currentPath,
      title,
      description: ogDescription ?? description,
      images: getOgImageUrl(props, currentPath),
    },
  }
}
